import axios from 'axios';

const getAuthToken = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session_token') {
      return value;
    }
  }
  return null;
};

const BFF_API_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';
const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';
export const bffClient = axios.create({
  baseURL: BFF_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

bffClient.interceptors.request.use(config => {
  const token = getAuthToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

bffClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      window.location.href = AUTH_APP_URL;
    }
    return Promise.reject(error);
  },
);

export type UserRole =
  | 'admin'
  | 'manager'
  | 'accountant'
  | 'receptionist'
  | 'waiter'
  | 'housekeeper'
  | 'maintenance_technician'
  | 'contract_officer'
  | 'shift_supervisor'
  | 'chef_de_partie'
  | 'spa_therapist'
  | 'hr_officer'
  | 'guest_liaison'
  | 'tour_agents';

export interface StaticDataOption {
  value: string;
  label: string;
}

export interface BffStaticData {
  roles: StaticDataOption[];
  departments: StaticDataOption[];
  employment_types: StaticDataOption[];
  employee_statuses: StaticDataOption[];
  account_statuses: StaticDataOption[];
}

export function roleLabelFromStaticData(roles: StaticDataOption[], value: string): string {
  return roles.find(role => role.value === value)?.label ?? value.replace(/_/g, ' ');
}
export type AccountStatus = 'active' | 'inactive' | 'suspended' | 'locked';
export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface Permission {
  id: string;
  resource: string;
  action: string;
  description?: string;
}

export interface PermissionMatrix {
  roles: string[];
  permissions: Permission[];
  matrix: Record<string, Record<string, string>>; // role -> permissionKey -> permissionID
  apps: Record<string, string[]>; // role -> appKeys
}

export interface BFFUser {
  id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  role: UserRole;
  status: AccountStatus;
  created_at: string;
  updated_at?: string;
}

export interface PaginatedUsers {
  users: BFFUser[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface CreateUserInput {
  email: string;
  username: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role?: UserRole;
}

export interface UpdateUserInput {
  first_name?: string;
  last_name?: string;
  phone?: string;
  role?: UserRole;
  status?: AccountStatus;
}

export interface BFFNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  read_at: string | null;
  resource_type: string | null;
  resource_id: string | null;
  priority: NotificationPriority;
  created_at: string;
}

export interface PaginatedNotifications {
  notifications: BFFNotification[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface SystemConfig {
  key: string;
  value: unknown;
  category: string;
  is_sensitive: boolean;
}

export interface Currency {
  code: string;
  name: string;
  symbol: string;
}

export interface CurrencySetting {
  id: string;
  code: string;
  name: string;
  symbol: string;
  exchange_rate: number;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CurrencyConvertResult {
  amount: number;
  from_currency: string;
  to_currency: string;
  converted: number;
  exchange_rate: number;
}

export type AuditAction =
  | 'login'
  | 'logout'
  | 'create'
  | 'update'
  | 'delete'
  | 'view'
  | 'approve'
  | 'reject';

export interface AuditLog {
  id: string;
  user_id: string | null;
  user_email: string | null;
  user_role: string | null;
  action: AuditAction;
  resource_type: string;
  resource_id: string | null;
  description: string | null;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  request_method: string | null;
  request_path: string | null;
  request_id: string | null;
  response_status: number | null;
  response_time_ms: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface PaginatedAuditLogs {
  data: AuditLog[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export const bffUserService = {
  list: async (params?: {
    page?: number;
    limit?: number;
    role?: UserRole;
    status?: AccountStatus;
  }): Promise<PaginatedUsers> => {
    const res = await bffClient.get<PaginatedUsers>('/api/v1/users', { params });
    return res.data;
  },

  get: (id: string) => bffClient.get<BFFUser>(`/api/v1/users/${id}`),

  create: (data: CreateUserInput) => bffClient.post<BFFUser>('/api/v1/auth/register', data),

  update: (id: string, data: UpdateUserInput) =>
    bffClient.put<BFFUser>(`/api/v1/users/${id}`, data),

  delete: (id: string) => bffClient.delete(`/api/v1/users/${id}`),

  lookupNames: async (
    ids: string[],
  ): Promise<{ users: Array<{ id: string; first_name: string; last_name: string }> }> => {
    const res = await bffClient.get<{
      users: Array<{ id: string; first_name: string; last_name: string }>;
    }>('/api/v1/users/names', { params: { ids: ids.join(',') } });
    return res.data;
  },

  listServers: async (): Promise<{
    users: Array<{ id: string; first_name: string; last_name: string; role: string }>;
  }> => {
    const res = await bffClient.get<{
      users: Array<{ id: string; first_name: string; last_name: string; role: string }>;
    }>('/api/v1/users/servers');
    return res.data;
  },
};

export const bffPermissionService = {
  getMatrix: () => bffClient.get<PermissionMatrix>('/api/v1/permissions/matrix'),

  update: (data: { role: string; permissions: string[]; apps: string[] }) =>
    bffClient.put('/api/v1/permissions', data),

  getAppsForRole: (role: string) =>
    bffClient.get<{ apps: string[] }>('/api/v1/permissions/apps', { params: { role } }),

  getPermissionsForRole: (role: string) =>
    bffClient.get<{ permissions: string[] }>('/api/v1/permissions/role', { params: { role } }),
};

export interface NotificationRecipient {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: UserRole;
}

export const bffNotificationService = {
  list: async (params?: { page?: number; limit?: number }): Promise<PaginatedNotifications> => {
    const res = await bffClient.get<PaginatedNotifications>('/api/v1/notifications', { params });
    return res.data;
  },

  getRecipients: async (): Promise<{ recipients: NotificationRecipient[] }> => {
    const res = await bffClient.get<{ recipients: NotificationRecipient[] }>(
      '/api/v1/notifications/recipients',
    );
    return res.data;
  },

  getUnreadCount: async (): Promise<{ unread_count: number }> => {
    const res = await bffClient.get<{ unread_count: number }>('/api/v1/notifications/unread-count');
    return res.data;
  },

  markAsRead: (id: string) => bffClient.put(`/api/v1/notifications/${id}/read`),

  markAllAsRead: () => bffClient.put('/api/v1/notifications/read-all'),

  create: (data: {
    user_id: string;
    type: string;
    title: string;
    message: string;
    resource_type?: string;
    resource_id?: string;
    priority?: string;
  }) => bffClient.post<BFFNotification>('/api/v1/notifications', data),

  sendToUser: (data: {
    to_user_id: string;
    type: string;
    title: string;
    message: string;
    priority?: string;
  }) => bffClient.post<BFFNotification>('/api/v1/notifications/send', data),
  broadcastToAdmins: (data: {
    type: string;
    title: string;
    message: string;
    priority?: string;
    resource_type?: string;
    resource_id?: string;
  }) => bffClient.post<BFFNotification>('/api/v1/notifications/broadcast-admin', data),

  updateItemStatusWithNotification: (
    itemId: string,
    data: {
      status: string;
      order_id?: string;
      product_name?: string;
      quantity?: number;
      notify_user_id?: string;
      notify_title?: string;
      notify_message?: string;
    },
  ): Promise<any> => {
    return bffClient.put(`/api/v1/food-orders/items/${itemId}/status`, data).then(res => res.data);
  },
};

export const bffConfigService = {
  getAll: async (): Promise<{ configs: SystemConfig[] }> => {
    const res = await bffClient.get<{ configs: SystemConfig[] }>('/api/v1/config');
    return res.data;
  },

  getByKey: (key: string) => bffClient.get<SystemConfig>(`/api/v1/config/${key}`),

  getCurrencies: async (): Promise<{ currencies: Currency[] }> => {
    const res = await bffClient.get<{ currencies: Currency[] }>('/api/v1/settings/currencies');
    return res.data;
  },

  getDefaultCurrency: async (): Promise<Currency> => {
    const res = await bffClient.get<Currency>('/api/v1/settings/currencies/default');
    return res.data;
  },

  setDefaultCurrency: (currencyCode: string) =>
    bffClient.put('/api/v1/settings/currencies/default', { currency_code: currencyCode }),

  updateConfig: (key: string, value: unknown) => bffClient.put(`/api/v1/config/${key}`, { value }),

  updateConfigs: (configs: Array<{ key: string; value: unknown }>) =>
    bffClient.put('/api/v1/config/batch', { configs }),
};

export const currencySettingsService = {
  list: async (): Promise<{ currencies: CurrencySetting[] }> => {
    const res = await bffClient.get<{ currencies: CurrencySetting[] }>('/api/v1/currency-settings');
    return res.data;
  },

  get: async (code: string): Promise<CurrencySetting> => {
    const res = await bffClient.get<CurrencySetting>(`/api/v1/currency-settings/${code}`);
    return res.data;
  },

  getDefault: async (): Promise<CurrencySetting> => {
    const res = await bffClient.get<CurrencySetting>('/api/v1/currency-settings/default');
    return res.data;
  },

  create: async (data: { code: string; name: string; symbol: string; exchange_rate: number }) => {
    const res = await bffClient.post<CurrencySetting>('/api/v1/currency-settings', data);
    return res.data;
  },

  update: async (
    code: string,
    data: Partial<Pick<CurrencySetting, 'name' | 'symbol' | 'exchange_rate' | 'is_active'>>,
  ) => {
    const res = await bffClient.put(`/api/v1/currency-settings/${code}`, data);
    return res.data;
  },

  delete: async (code: string) => {
    const res = await bffClient.delete(`/api/v1/currency-settings/${code}`);
    return res.data;
  },

  setDefault: async (code: string) => {
    const res = await bffClient.put(`/api/v1/currency-settings/${code}/default`);
    return res.data;
  },

  convert: async (amount: number, from: string, to: string): Promise<CurrencyConvertResult> => {
    const res = await bffClient.post<CurrencyConvertResult>('/api/v1/currency-settings/convert', {
      amount,
      from_currency: from,
      to_currency: to,
    });
    return res.data;
  },
};

export const bffAuditService = {
  list: (params?: {
    page?: number;
    limit?: number;
    action?: AuditAction;
    resource_type?: string;
    user_id?: string;
    search?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<PaginatedAuditLogs> => {
    return bffClient
      .get<PaginatedAuditLogs>('/api/v1/audit-logs', { params })
      .then(res => res.data);
  },

  listByUser: (
    userId: string,
    params?: {
      page?: number;
      limit?: number;
    },
  ): Promise<PaginatedAuditLogs> => {
    return bffClient
      .get<PaginatedAuditLogs>(`/api/v1/audit-logs/user/${userId}`, { params })
      .then(res => res.data);
  },

  listByResource: (
    resourceType: string,
    resourceId: string,
    params?: {
      page?: number;
      limit?: number;
    },
  ): Promise<PaginatedAuditLogs> => {
    return bffClient
      .get<PaginatedAuditLogs>(`/api/v1/audit-logs/resource/${resourceType}/${resourceId}`, {
        params,
      })
      .then(res => res.data);
  },
};

export interface SocialMediaPost {
  id: string;
  title: string;
  content: string;
  media_urls: string[];
  platforms: string[];
  scheduled_at: string | null;
  published_at: string | null;
  status: 'draft' | 'scheduled' | 'published' | 'failed';
  impressions: number;
  likes: number;
  comments: number;
  shares: number;
  clicks: number;
  post_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocialMediaPostListResponse {
  posts: SocialMediaPost[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface SocialMediaAnalytics {
  total_impressions: number;
  total_likes: number;
  total_comments: number;
  total_shares: number;
  total_clicks: number;
  published_count: number;
  scheduled_count: number;
  draft_count: number;
}

export interface CreateSocialPostInput {
  title: string;
  content: string;
  media_urls?: string[];
  platforms: string[];
  scheduled_at?: string;
  status?: string;
}

export interface UpdateSocialPostInput {
  id: string;
  title?: string;
  content?: string;
  media_urls?: string[];
  platforms?: string[];
  scheduled_at?: string;
  status?: string;
}

export const bffSocialPostService = {
  list: (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<SocialMediaPostListResponse> => {
    return bffClient
      .get<SocialMediaPostListResponse>('/api/v1/social-posts', { params })
      .then(res => res.data);
  },

  get: (id: string): Promise<SocialMediaPost> => {
    return bffClient.get<SocialMediaPost>(`/api/v1/social-posts/${id}`).then(res => res.data);
  },

  create: (data: CreateSocialPostInput): Promise<SocialMediaPost> => {
    return bffClient.post<SocialMediaPost>('/api/v1/social-posts', data).then(res => res.data);
  },

  update: (data: UpdateSocialPostInput): Promise<SocialMediaPost> => {
    return bffClient
      .put<SocialMediaPost>(`/api/v1/social-posts/${data.id}`, data)
      .then(res => res.data);
  },

  delete: (id: string) => {
    return bffClient.delete(`/api/v1/social-posts/${id}`);
  },

  publish: (id: string) => {
    return bffClient.post(`/api/v1/social-posts/${id}/publish`);
  },

  getAnalytics: (): Promise<SocialMediaAnalytics> => {
    return bffClient
      .get<SocialMediaAnalytics>('/api/v1/social-posts/analytics')
      .then(res => res.data);
  },
};

// --- BFF Inventory Proxy ---

export interface BFFPurchaseOrder {
  id: string;
  po_number: string;
  supplier_id: string;
  supplier?: BFFSupplier;
  order_date: string;
  expected_delivery_date: string | null;
  actual_delivery_date: string | null;
  status: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  delivery_address: string;
  delivery_notes: string;
  payment_status: string;
  payment_reference: string;
  notes: string;
  internal_notes: string;
  accountant_approved_by: string | null;
  accountant_approved_at: string | null;
  accountant_rejection_reason: string | null;
  cancellation_notes: string | null;
  payment_history: string | null;
  items: BFFPurchaseOrderItem[];
  created_at: string;
  updated_at: string;
}

export interface BFFPurchaseOrderItem {
  id: string;
  purchase_order_id: string;
  inventory_item_id: string | null;
  product_id: string | null;
  item_name: string;
  sku: string;
  quantity_ordered: number;
  quantity_received: number;
  unit_cost: number;
  total_cost: number;
  is_fully_received: boolean;
}

export interface BFFSupplier {
  id: string;
  name: string;
  code: string;
  contact_person: string;
  phone: string;
  email: string;
  payment_terms: string;
  status: string;
}

export interface BFFInventoryValuation {
  total_value: number;
  total_items: number;
  by_location: { location: string; value: number; count: number }[];
  by_category: { category: string; value: number; count: number }[];
  items: {
    id: string;
    sku: string;
    name: string;
    quantity: number;
    unit_cost: number;
    total_value: number;
    location: string;
    minimum_quantity: number;
    reorder_point: number;
  }[];
}

export const bffInventoryService = {
  purchaseOrders: {
    list: (params?: {
      status?: string;
      page?: number;
      page_size?: number;
    }): Promise<{
      purchase_orders: BFFPurchaseOrder[];
      total: number;
      page: number;
      page_size: number;
      total_pages: number;
    }> => {
      return bffClient.get('/api/v1/purchase-orders', { params }).then(res => res.data);
    },
    get: (id: string): Promise<BFFPurchaseOrder> => {
      return bffClient.get<BFFPurchaseOrder>(`/api/v1/purchase-orders/${id}`).then(res => res.data);
    },
    accountantApprove: (id: string) => {
      return bffClient.post(`/api/v1/purchase-orders/${id}/accountant-approve`);
    },
    accountantReject: (id: string, reason: string) => {
      return bffClient.post(`/api/v1/purchase-orders/${id}/accountant-reject`, { reason });
    },
    updatePayment: (
      id: string,
      paymentStatus: string,
      paymentReference: string,
      amount?: number,
    ) => {
      return bffClient.patch(`/api/v1/purchase-orders/${id}/payment`, {
        payment_status: paymentStatus,
        payment_reference: paymentReference,
        amount,
      });
    },
  },
  valuation: {
    get: (): Promise<BFFInventoryValuation> => {
      return bffClient
        .get<BFFInventoryValuation>('/api/v1/finance/inventory-valuation')
        .then(res => res.data);
    },
  },
};

// --- BFF Staff Performance ---

export interface StaffPerformance {
  user_id: string;
  user_first_name: string;
  user_last_name: string;
  user_role: string;
  total_orders: number;
  completed_orders: number;
  pending_orders: number;
  cancelled_orders: number;
  total_revenue: number;
  period_start?: string;
  period_end?: string;
}

export interface StaffPerformanceResponse {
  data: StaffPerformance[];
  period_start?: string;
  period_end?: string;
}

export const bffStaffPerformanceService = {
  getStaffPerformance: (params: {
    user_id?: string;
    start_date: string;
    end_date: string;
  }): Promise<StaffPerformanceResponse> => {
    return bffClient.get<StaffPerformance[]>('/api/v1/staff-performance', { params }).then(res => ({
      data: res.data,
    }));
  },

  getAllStaffPerformance: (params: {
    start_date: string;
    end_date: string;
  }): Promise<StaffPerformanceResponse> => {
    return bffClient
      .get<StaffPerformance[]>('/api/v1/staff-performance/all', { params })
      .then(res => ({
        data: res.data,
      }));
  },

  getPermissionMatrix: (): Promise<{
    roles: string[];
    permissions: Array<{ id: string; resource: string; action: string }>;
    matrix: Record<string, Record<string, string>>;
    apps: Record<string, string[]>;
  }> => {
    return bffClient.get('/api/v1/permissions/matrix').then(res => res.data);
  },

  updatePermissions: (data: {
    role: string;
    permissions: string[];
    apps: string[];
  }): Promise<void> => {
    return bffClient.put('/api/v1/permissions', data).then(res => res.data);
  },

  getAppsForRole: (role: string): Promise<{ apps: string[] }> => {
    return bffClient.get('/api/v1/permissions/apps', { params: { role } }).then(res => res.data);
  },

  getPermissionsForRole: (role: string): Promise<{ permissions: string[] }> => {
    return bffClient.get('/api/v1/permissions/role', { params: { role } }).then(res => res.data);
  },
};

export interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: string;
  department: string;
  position: string;
  start_date: string;
  status: string;
  hourly_rate?: number;
  manager_id?: string;
  user_id?: string;
  created_at: string;
  updated_at?: string;
  audit_logs?: AuditLog[];
}

export interface ShiftAssignment {
  id: string;
  employee_id: string;
  shift_date: string;
  start_time: string;
  end_time: string;
  role: string;
  status: string;
  break_minutes: number;
  notes?: string;
  created_at: string;
}

export interface LeaveRequest {
  id: string;
  employee_id: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  total_days: number;
  reason: string;
  status: string;
  approved_by?: string;
  approved_at?: string;
  created_at: string;
}

export interface AccessHandoff {
  id: string;
  from_user_id: string;
  to_user_id: string;
  access_level: string;
  reason: string;
  expiry_time: string;
  status: string;
  created_at: string;
}

export const bffEmployeesService = {
  list: (params?: {
    page?: number;
    limit?: number;
  }): Promise<{
    employees: Employee[];
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  }> => {
    return bffClient.get('/api/v1/employees', { params }).then(res => res.data);
  },

  get: (id: string): Promise<Employee> => {
    return bffClient.get(`/api/v1/employees/${id}`).then(res => res.data);
  },

  create: (data: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    role: string;
    department?: string;
    position?: string;
    hourly_rate?: number;
  }): Promise<Employee> => {
    return bffClient.post('/api/v1/employees', data).then(res => res.data);
  },

  update: (
    id: string,
    data: Partial<{
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
      role: string;
      department: string;
      position: string;
      status: string;
      hourly_rate: number;
    }>,
  ): Promise<Employee> => {
    return bffClient.put(`/api/v1/employees/${id}`, data).then(res => res.data);
  },

  delete: (id: string): Promise<{ message: string }> => {
    return bffClient.delete(`/api/v1/employees/${id}`).then(res => res.data);
  },
};

export const bffShiftsService = {
  list: (params?: {
    employee_id?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<{ shifts: ShiftAssignment[] }> => {
    return bffClient.get('/api/v1/shifts', { params }).then(res => res.data);
  },

  listMine: (): Promise<{ shifts: ShiftAssignment[] }> => {
    return bffClient.get('/api/v1/my/shifts').then(res => res.data);
  },

  create: (data: {
    employee_id: string;
    shift_date: string;
    start_time: string;
    end_time: string;
    role?: string;
    break_minutes?: number;
    notes?: string;
  }): Promise<ShiftAssignment> => {
    return bffClient.post('/api/v1/shifts', data).then(res => res.data);
  },

  update: (
    id: string,
    data: Partial<{
      employee_id: string;
      shift_date: string;
      start_time: string;
      end_time: string;
      role: string;
      status: string;
      break_minutes: number;
      notes: string;
    }>,
  ): Promise<ShiftAssignment> => {
    return bffClient.put(`/api/v1/shifts/${id}`, data).then(res => res.data);
  },

  delete: (id: string): Promise<{ message: string }> => {
    return bffClient.delete(`/api/v1/shifts/${id}`).then(res => res.data);
  },
};

export const bffLeavesService = {
  list: (params?: { status?: string }): Promise<{ leaves: LeaveRequest[] }> => {
    return bffClient.get('/api/v1/leaves', { params }).then(res => res.data);
  },

  create: (data: {
    employee_id: string;
    leave_type: string;
    start_date: string;
    end_date: string;
    reason?: string;
  }): Promise<LeaveRequest> => {
    return bffClient.post('/api/v1/leaves', data).then(res => res.data);
  },

  update: (
    id: string,
    data: {
      employee_id?: string;
      leave_type?: string;
      start_date?: string;
      end_date?: string;
      reason?: string;
      status?: string;
    },
  ): Promise<LeaveRequest> => {
    return bffClient.put(`/api/v1/leaves/${id}`, data).then(res => res.data);
  },

  approve: (id: string): Promise<{ message: string }> => {
    return bffClient.post(`/api/v1/leaves/${id}/approve`).then(res => res.data);
  },

  reject: (id: string): Promise<{ message: string }> => {
    return bffClient.post(`/api/v1/leaves/${id}/reject`).then(res => res.data);
  },

  cancel: (id: string): Promise<{ message: string }> => {
    return bffClient.post(`/api/v1/leaves/${id}/cancel`).then(res => res.data);
  },
};

export interface LeaveBalance {
  id: string;
  employee_id: string;
  leave_type: string;
  total_days: number;
  used_days: number;
  remaining_days: number;
  year: number;
  created_at: string;
  updated_at: string;
}

/**
 * BFF/system email templates (HR, PO, events, etc.).
 * Named SystemEmailTemplate to avoid clashing with communication-hub EmailTemplate.
 */
export interface SystemEmailTemplate {
  id: string;
  name: string;
  tag: string;
  type: 'po' | 'staff' | 'event' | 'notification' | string;
  subject: string;
  body_html: string;
  html_body?: string;
  variables: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const bffLeaveBalancesService = {
  list: (params?: { employee_id?: string }): Promise<{ balances: LeaveBalance[] }> => {
    return bffClient.get('/api/v1/leave-balances', { params }).then(res => res.data);
  },

  create: (data: {
    employee_id: string;
    annual: number;
    sick: number;
    off_days: number;
    maternity: number;
    compassionate: number;
  }): Promise<LeaveBalance> => {
    return bffClient.post('/api/v1/leave-balances', data).then(res => res.data);
  },

  update: (
    employeeId: string,
    data: {
      employee_id?: string;
      annual: number;
      sick: number;
      off_days: number;
      maternity: number;
      compassionate: number;
    },
  ): Promise<LeaveBalance> => {
    return bffClient.put(`/api/v1/leave-balances/${employeeId}`, data).then(res => res.data);
  },

  delete: (id: string): Promise<{ message: string }> => {
    return bffClient.delete(`/api/v1/leave-balances/${id}`).then(res => res.data);
  },
};

export const bffAccessHandoffsService = {
  list: (): Promise<{ handovers: AccessHandoff[] }> => {
    return bffClient.get('/api/v1/access-handovers').then(res => res.data);
  },

  create: (data: {
    to_user_id: string;
    access_level: string;
    reason?: string;
    hours?: number;
  }): Promise<AccessHandoff> => {
    return bffClient.post('/api/v1/access-handovers', data).then(res => res.data);
  },

  revoke: (id: string): Promise<{ message: string }> => {
    return bffClient.post(`/api/v1/access-handovers/${id}/revoke`).then(res => res.data);
  },
};

export interface ProvisionUserRequest {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  role?: string;
  department?: string;
}

export interface ProvisionUserResponse {
  id: string;
  email: string;
  username: string;
  password: string;
  role: string;
  message: string;
}

export const BFF_STATIC_DATA_QUERY_KEY = ['bff-static-data'] as const;

export const bffStaticDataService = {
  get: (): Promise<BffStaticData> => {
    return bffClient.get('/api/v1/static-data').then(res => res.data);
  },
};

export const bffHRService = {
  listRoles: async (): Promise<{ roles: string[] }> => {
    const data = await bffStaticDataService.get();
    return { roles: data.roles.map(role => role.value) };
  },

  provisionUser: (data: ProvisionUserRequest): Promise<ProvisionUserResponse> => {
    return bffClient.post('/api/v1/hr/provision-user', data).then(res => res.data);
  },
};

export const bffEmailService = {
  listTemplates: (): Promise<SystemEmailTemplate[]> => {
    return bffClient.get('/api/v1/email/templates').then(res => res.data);
  },

  getTemplate: (id: string): Promise<SystemEmailTemplate> => {
    return bffClient.get(`/api/v1/email/templates/${id}`).then(res => res.data);
  },

  getTemplatesByType: (type: string): Promise<SystemEmailTemplate[]> => {
    return bffClient.get(`/api/v1/email/templates/type/${type}`).then(res => res.data);
  },

  createTemplate: (data: Partial<SystemEmailTemplate>): Promise<SystemEmailTemplate> => {
    return bffClient.post('/api/v1/email/templates', data).then(res => res.data);
  },

  updateTemplate: (
    id: string,
    data: Partial<SystemEmailTemplate>,
  ): Promise<SystemEmailTemplate> => {
    return bffClient.put(`/api/v1/email/templates/${id}`, data).then(res => res.data);
  },

  deleteTemplate: (id: string): Promise<{ message: string }> => {
    return bffClient.delete(`/api/v1/email/templates/${id}`).then(res => res.data);
  },

  sendEmail: (data: {
    template_name: string;
    data: Record<string, unknown>;
    recipients: string[];
  }): Promise<{ message: string }> => {
    return bffClient.post('/api/v1/email/send', data).then(res => res.data);
  },

  sendCustomEmail: (data: {
    subject: string;
    body_html: string;
    recipients: string[];
  }): Promise<{ message: string }> => {
    return bffClient.post('/api/v1/email/send-custom', data).then(res => res.data);
  },
};

export const bffAIService = {
  refine: (data: { input: string; task: string }): Promise<{ result: string }> => {
    return bffClient.post('/api/v1/ai/refine', data).then(res => res.data);
  },

  structured: (data: { input: string; schema: string }): Promise<{ result: string }> => {
    return bffClient.post('/api/v1/ai/structured', data).then(res => res.data);
  },
};

export type WhatsAppBookingMode = 'start' | 'room' | 'event';
export type WhatsAppBookingSender = 'guest' | 'concierge';

export interface WhatsAppBookingMessage {
  from: WhatsAppBookingSender;
  text: string;
  time?: string;
}

export interface WhatsAppBookingFlow {
  mode: WhatsAppBookingMode;
  title: string;
  app: string;
  first_message: string;
  prompts: string[];
  schema: Record<string, string>;
  sample_replies: string[];
}

export interface WhatsAppBookingTurnRequest {
  mode: WhatsAppBookingMode;
  messages: WhatsAppBookingMessage[];
  prompts_consumed?: number;
}

export interface WhatsAppBookingTurnResponse {
  mode: WhatsAppBookingMode;
  app: string;
  reply: string;
  prompt_index: number;
  is_complete: boolean;
  flow_complete: boolean;
  schema: Record<string, string>;
  extracted?: Record<string, unknown>;
  raw_ai_result?: string;
  ai_error?: string;
  detected_mode?: WhatsAppBookingMode;
}

export const bffWhatsAppBookingService = {
  flows: (): Promise<{ flows: Record<WhatsAppBookingMode, WhatsAppBookingFlow> }> => {
    return bffClient.get('/api/v1/ai/whatsapp-booking/flows').then(res => res.data);
  },

  turn: (data: WhatsAppBookingTurnRequest): Promise<WhatsAppBookingTurnResponse> => {
    return bffClient.post('/api/v1/ai/whatsapp-booking/turn', data).then(res => res.data);
  },

  submit: (data: {
    mode: WhatsAppBookingMode;
    extracted: Record<string, unknown>;
    messages: WhatsAppBookingMessage[];
  }): Promise<{ message: string; lead_id?: string }> => {
    return bffClient.post('/api/v1/ai/whatsapp-booking/submit', data).then(res => res.data);
  },
};

export interface BackupLog {
  id: string;
  file_name: string;
  file_size: number;
  type: 'manual' | 'auto';
  status: 'success' | 'failed';
  storage: string;
  error?: string;
  created_at: string;
}

export const bffBackupService = {
  create: (type?: string) =>
    bffClient
      .post<BackupLog>(`/api/v1/backup/create?type=${type || 'manual'}`)
      .then(res => res.data),
  list: (params?: { page?: number; limit?: number }) =>
    bffClient.get('/api/v1/backup/logs', { params }).then(res => res.data),
  restore: (id: string) => bffClient.post(`/api/v1/backup/restore/${id}`).then(res => res.data),
  delete: (id: string) => bffClient.delete(`/api/v1/backup/${id}`).then(res => res.data),
  sync: (id: string) => bffClient.post(`/api/v1/backup/sync/${id}`).then(res => res.data),
  getConfig: () => bffClient.get('/api/v1/backup/config').then(res => res.data),
  updateConfig: (config: Record<string, unknown>) =>
    bffClient.put('/api/v1/backup/config', config).then(res => res.data),
  upload: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return bffClient
      .post<BackupLog>('/api/v1/backup/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(res => res.data);
  },
  downloadUrl: (id: string) => `${bffClient.defaults.baseURL}/api/v1/backup/${id}/download`,
};

export interface WhatsAppSendDocumentRequest {
  to: string;
  body: string;
  pdf_base64: string;
  filename: string;
  reference_type?: string;
  reference_id?: string;
}

export interface WhatsAppSendResponse {
  status: string;
  sid?: string;
  to?: string;
  media_url?: string;
  message: string;
}

export interface WhatsAppTestConnectionResponse {
  status: 'success' | 'error' | 'not_configured';
  message: string;
}

export const bffWhatsAppService = {
  sendDocument: (data: WhatsAppSendDocumentRequest): Promise<WhatsAppSendResponse> =>
    bffClient.post('/api/v1/whatsapp/send-document', data).then(res => res.data),

  sendText: (data: { to: string; body: string }): Promise<WhatsAppSendResponse> =>
    bffClient.post('/api/v1/whatsapp/send-text', data).then(res => res.data),

  sendTemplate: (data: {
    to: string;
    template_sid: string;
    language?: string;
    variables?: Record<string, string>;
  }): Promise<WhatsAppSendResponse> =>
    bffClient.post('/api/v1/whatsapp/send-template', data).then(res => res.data),

  testConnection: (): Promise<WhatsAppTestConnectionResponse> =>
    bffClient.post('/api/v1/whatsapp/test-connection').then(res => res.data),
};

// ---- Africa Verify (ID Verification) ----

export interface UploadInvite {
  id: string;
  session_id: string;
  purpose: 'document' | 'statement' | string;
  document_type: string;
  expires_at: string;
  opened_at?: string | null;
  uploaded_at?: string | null;
  revoked_at?: string | null;
  attempt_count: number;
  last_error: string;
  created_by: string;
  created_at: string;
  status: 'open' | 'uploaded' | 'expired' | 'revoked' | string;
}

export interface PublicUploadInvite {
  purpose: 'document' | 'statement' | string;
  document_type: string;
  label: string;
  expires_at: string;
  status: 'open' | 'uploaded' | 'expired' | 'revoked' | string;
  needs_pin: boolean;
  country?: string;
}

export interface VerificationSession {
  id: string;
  status: string;
  guest_name: string;
  guest_email: string;
  notes: string;
  source?: 'desk' | 'link' | string;
  created_by: string;
  created_at: string;
  updated_at: string;
  documents?: VerificationDocument[];
  decisions?: VerificationDecision[];
  upload_invite?: UploadInvite | null;
}

export interface VerificationDocument {
  id: string;
  session_id: string;
  document_type: string;
  file_name: string;
  ocr_raw_text: string;
  ocr_confidence: number;
  image_quality_score: number;
  status: string;
  created_at: string;
  fields?: DocumentField[];
  validation_results?: ValidationResult[];
  risk_assessment?: RiskAssessment;
}

export interface DocumentField {
  id: string;
  document_id: string;
  field_name: string;
  field_value: string;
  confidence: number;
  is_normalized: boolean;
  original_value: string;
}

export interface ValidationResult {
  id: string;
  document_id: string;
  rule_name: string;
  rule_category: string;
  passed: boolean;
  message: string;
  severity: string;
  score_impact: number;
  metadata?: Record<string, unknown>;
}

export interface RiskAssessment {
  id: string;
  document_id: string;
  total_score: number;
  recommendation: string;
  factors: Record<string, unknown>;
  assessed_at: string;
}

export interface VerificationDecision {
  id: string;
  session_id: string;
  action: string;
  reason: string;
  decided_by: string;
  decided_at: string;
}

export interface VerificationComment {
  id: string;
  session_id: string;
  author: string;
  body: string;
  created_at: string;
}

export interface VerificationConnector {
  id: string;
  name: string;
  description: string;
  endpoint: string;
  http_method: string;
  auth_header_name: string;
  auth_scheme: string;
  api_key_hint: string;
  extra_headers?: Record<string, unknown>;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ConnectorDelivery {
  id: string;
  connector_id: string;
  session_id: string;
  status: 'success' | 'failed' | string;
  http_status?: number | null;
  request_payload?: Record<string, unknown>;
  response_body?: string;
  error_message?: string;
  sent_by: string;
  sent_at: string;
}

export interface CreateConnectorInput {
  name: string;
  description?: string;
  endpoint: string;
  http_method?: string;
  auth_header_name?: string;
  auth_scheme?: string;
  api_key: string;
  extra_headers?: Record<string, unknown>;
  is_active?: boolean;
}

export interface UpdateConnectorInput {
  name?: string;
  description?: string;
  endpoint?: string;
  http_method?: string;
  auth_header_name?: string;
  auth_scheme?: string;
  api_key?: string;
  extra_headers?: Record<string, unknown>;
  is_active?: boolean;
}

export const africaVerifyService = {
  createSession: async (data: {
    guest_name?: string;
    guest_email?: string;
    notes?: string;
  }): Promise<VerificationSession> => {
    const res = await bffClient.post<VerificationSession>('/api/v1/id-verify/sessions', data);
    return res.data;
  },

  listSessions: async (params?: {
    page?: number;
    page_size?: number;
    status?: string;
  }): Promise<{
    data: VerificationSession[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> => {
    const res = await bffClient.get('/api/v1/id-verify/sessions', { params });
    return res.data;
  },

  getSession: async (id: string): Promise<VerificationSession> => {
    const res = await bffClient.get<VerificationSession>(`/api/v1/id-verify/sessions/${id}`);
    return res.data;
  },

  updateSession: async (
    id: string,
    data: Partial<Pick<VerificationSession, 'guest_name' | 'guest_email' | 'notes' | 'status'>>,
  ): Promise<VerificationSession> => {
    const res = await bffClient.put<VerificationSession>(`/api/v1/id-verify/sessions/${id}`, data);
    return res.data;
  },

  deleteSession: async (id: string): Promise<void> => {
    await bffClient.delete(`/api/v1/id-verify/sessions/${id}`);
  },

  scanDocument: async (
    sessionId: string,
    file: File,
    documentType: string,
    password?: string,
    bank?: string,
    country?: string,
  ): Promise<{
    document: VerificationDocument;
    validation_results: ValidationResult[];
    risk_assessment: RiskAssessment;
  }> => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('document_type', documentType);
    if (password) {
      formData.append('password', password);
    }
    if (bank) {
      formData.append('bank', bank);
    }
    if (country) {
      formData.append('country', country);
    }
    const res = await bffClient.post(
      `/api/v1/id-verify/sessions/${sessionId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  },

  scanDocumentBase64: async (
    sessionId: string,
    imageBase64: string,
    documentType: string,
  ): Promise<{
    document: VerificationDocument;
    validation_results: ValidationResult[];
    risk_assessment: RiskAssessment;
  }> => {
    const res = await bffClient.post(`/api/v1/id-verify/sessions/${sessionId}/documents/base64`, {
      image_base64: imageBase64,
      document_type: documentType,
    });
    return res.data;
  },

  listDocuments: async (sessionId: string): Promise<{ documents: VerificationDocument[] }> => {
    const res = await bffClient.get(`/api/v1/id-verify/sessions/${sessionId}/documents`);
    return res.data;
  },

  getDocument: async (documentId: string): Promise<VerificationDocument> => {
    const res = await bffClient.get<VerificationDocument>(
      `/api/v1/id-verify/documents/${documentId}`,
    );
    return res.data;
  },

  deleteDocument: async (documentId: string): Promise<void> => {
    await bffClient.delete(`/api/v1/id-verify/documents/${documentId}`);
  },

  approveSession: async (
    sessionId: string,
    reason?: string,
  ): Promise<{ decision: VerificationDecision; session: VerificationSession }> => {
    const res = await bffClient.post(`/api/v1/id-verify/sessions/${sessionId}/decisions/approve`, {
      action: 'approve',
      reason,
    });
    return res.data;
  },

  rejectSession: async (
    sessionId: string,
    reason?: string,
  ): Promise<{ decision: VerificationDecision; session: VerificationSession }> => {
    const res = await bffClient.post(`/api/v1/id-verify/sessions/${sessionId}/decisions/reject`, {
      action: 'reject',
      reason,
    });
    return res.data;
  },

  getDecisions: async (sessionId: string): Promise<{ decisions: VerificationDecision[] }> => {
    const res = await bffClient.get(`/api/v1/id-verify/sessions/${sessionId}/decisions`);
    return res.data;
  },

  addComment: async (sessionId: string, body: string): Promise<VerificationComment> => {
    const res = await bffClient.post(`/api/v1/id-verify/sessions/${sessionId}/comments`, { body });
    return res.data;
  },

  listComments: async (sessionId: string): Promise<{ comments: VerificationComment[] }> => {
    const res = await bffClient.get(`/api/v1/id-verify/sessions/${sessionId}/comments`);
    return res.data;
  },

  deleteComment: async (commentId: string): Promise<void> => {
    await bffClient.delete(`/api/v1/id-verify/comments/${commentId}`);
  },

  // ---- Outbound connectors ----

  listConnectors: async (params?: {
    active_only?: boolean;
  }): Promise<{ connectors: VerificationConnector[] }> => {
    const res = await bffClient.get('/api/v1/id-verify/connectors', {
      params: params?.active_only ? { active_only: true } : undefined,
    });
    return res.data;
  },

  getConnector: async (id: string): Promise<VerificationConnector> => {
    const res = await bffClient.get<VerificationConnector>(`/api/v1/id-verify/connectors/${id}`);
    return res.data;
  },

  createConnector: async (data: CreateConnectorInput): Promise<VerificationConnector> => {
    const res = await bffClient.post<VerificationConnector>('/api/v1/id-verify/connectors', data);
    return res.data;
  },

  updateConnector: async (
    id: string,
    data: UpdateConnectorInput,
  ): Promise<VerificationConnector> => {
    const res = await bffClient.put<VerificationConnector>(
      `/api/v1/id-verify/connectors/${id}`,
      data,
    );
    return res.data;
  },

  deleteConnector: async (id: string): Promise<void> => {
    await bffClient.delete(`/api/v1/id-verify/connectors/${id}`);
  },

  exportSession: async (
    sessionId: string,
    documentId?: string,
  ): Promise<Record<string, unknown>> => {
    const res = await bffClient.get<Record<string, unknown>>(
      `/api/v1/id-verify/sessions/${sessionId}/export`,
      { params: documentId ? { document_id: documentId } : undefined },
    );
    return res.data;
  },

  sendSession: async (
    sessionId: string,
    connectorId: string,
    documentId?: string,
  ): Promise<{ success: boolean; delivery: ConnectorDelivery }> => {
    const res = await bffClient.post(`/api/v1/id-verify/sessions/${sessionId}/send`, {
      connector_id: connectorId,
      document_id: documentId || undefined,
    });
    return res.data;
  },

  listSessionDeliveries: async (
    sessionId: string,
  ): Promise<{ deliveries: ConnectorDelivery[] }> => {
    const res = await bffClient.get(`/api/v1/id-verify/sessions/${sessionId}/deliveries`);
    return res.data;
  },

  // ---- Affordability Analysis ----

  statementOpinion: async (
    sessionId: string,
    brief: string,
    onDelta?: (text: string) => void,
    signal?: AbortSignal,
  ): Promise<StatementOpinion> => {
    const token = getAuthToken();
    const res = await fetch(`${BFF_API_URL}/api/v1/id-verify/sessions/${sessionId}/opinion`, {
      method: 'POST',
      credentials: 'include',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ brief }),
    });
    if (res.status === 401 && typeof window !== 'undefined') {
      window.location.href = AUTH_APP_URL;
    }
    if (!res.ok) {
      throw new Error(`Statement opinion failed (${res.status})`);
    }
    if (!res.body) {
      throw new Error('Statement opinion stream is missing');
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let pending = '';
    let opinion = '';
    let stance = '';
    let available = false;
    const takeEvent = (block: string) => {
      let event = 'message';
      const dataLines: string[] = [];
      for (const line of block.split(/\r?\n/)) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
      }
      if (!dataLines.length) return;
      let data: { text?: string; available?: boolean; stance?: string } = {};
      try {
        data = JSON.parse(dataLines.join('\n'));
      } catch {
        return;
      }
      if (event === 'delta' && data.text) {
        opinion += data.text;
        onDelta?.(data.text);
      }
      if (event === 'done' || event === 'unavailable') {
        available = Boolean(data.available);
        stance = data.stance || stance;
      }
    };

    while (true) {
      const { value, done } = await reader.read();
      pending += decoder.decode(value || new Uint8Array(), { stream: !done });
      const blocks = pending.split(/\r?\n\r?\n/);
      pending = blocks.pop() || '';
      for (const block of blocks) takeEvent(block);
      if (done) break;
    }
    if (pending.trim()) takeEvent(pending);
    return { available, opinion: opinion.trim(), stance };
  },

  analyzeAffordability: async (
    sessionId: string,
    data?: {
      currency?: string;
    },
  ): Promise<{ success: boolean; report: AffordabilityReport }> => {
    const res = await bffClient.post(
      `/api/v1/id-verify/sessions/${sessionId}/affordability`,
      data || {},
    );
    return res.data;
  },

  getAffordabilityReport: async (
    sessionId: string,
  ): Promise<{ success: boolean; report: AffordabilityReport }> => {
    const res = await bffClient.get(`/api/v1/id-verify/sessions/${sessionId}/affordability`);
    return res.data;
  },

  getAffordabilityTransactions: async (
    sessionId: string,
    params?: { month?: string; category?: string; page?: number; page_size?: number },
  ): Promise<{ success: boolean; transactions: CategorizedTransaction[]; total: number }> => {
    const res = await bffClient.get(
      `/api/v1/id-verify/sessions/${sessionId}/affordability/transactions`,
      { params },
    );
    return res.data;
  },

  deleteAffordabilityReport: async (sessionId: string): Promise<void> => {
    await bffClient.delete(`/api/v1/id-verify/sessions/${sessionId}/affordability`);
  },

  createUploadInvite: async (data: {
    purpose: 'document' | 'statement' | 'logbook';
    document_type: string;
    guest_name?: string;
    guest_email?: string;
    expires_in_hours?: number;
    country?: string;
    session_id?: string;
  }): Promise<{
    invite: UploadInvite;
    token: string;
    upload_path: string;
    session_id: string;
  }> => {
    const res = await bffClient.post('/api/v1/id-verify/upload-invites', data);
    return res.data;
  },

  revokeUploadInvite: async (id: string): Promise<{ invite: UploadInvite }> => {
    const res = await bffClient.post(`/api/v1/id-verify/upload-invites/${id}/revoke`);
    return res.data;
  },
};

async function publicUploadError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (body && typeof body.error === 'string' && body.error) return body.error;
  } catch {
    // The body is not JSON.
  }
  return 'Something went wrong. Try again.';
}

/** Opens a public upload link. Does not send a staff session. */
export async function getPublicUpload(token: string): Promise<PublicUploadInvite> {
  const res = await fetch(`${BFF_API_URL}/api/v1/id-verify/uploads/${encodeURIComponent(token)}`);
  if (!res.ok) throw new Error(await publicUploadError(res));
  return res.json();
}

/** Sends one file through a public upload link. The response has no extracted data. */
export async function submitPublicUpload(
  token: string,
  file: File,
  password?: string,
  bank?: string,
): Promise<void> {
  const body = new FormData();
  body.append('file', file);
  if (password) body.append('password', password);
  if (bank) body.append('bank', bank);
  const res = await fetch(`${BFF_API_URL}/api/v1/id-verify/uploads/${encodeURIComponent(token)}`, {
    method: 'POST',
    body,
  });
  if (!res.ok) throw new Error(await publicUploadError(res));
}

// ---- Affordability Types ----

export interface FinancialMetrics {
  monthly_income: number | null;
  income_stability: number | null;
  monthly_expenses: number | null;
  free_cash_flow: number | null;
  existing_debt: number | null;
  monthly_debt_payment: number | null;
  dti: number | null;
  average_bank_balance: number | null;
  cash_buffer_months: number | null;
  mpesa_monthly_inflow: number | null;
  income_sources: number | null;
  employment_months: number | null;
  late_payments: number | null;
  document_income_match: boolean | null;
  /** Percent of qualified income. Absent when income could not be measured. */
  gambling_of_income?: number | null;
  savings_rate?: number | null;
  essential_burden?: number | null;
  /** Agent withdrawals as a percent of all withdrawals. */
  cash_out_ratio?: number | null;
  airtime_of_income?: number | null;
  fuliza_draws_per_month?: number | null;
  /** Repaid mobile-loan amount as a percent of the amount disbursed. */
  repayment_coverage?: number | null;
  lowest_balance?: number | null;
  deficit_months?: number | null;
  /** Largest income source as a percent of qualified income. */
  income_concentration?: number | null;
  /** Average percent difference between parsed rows and the printed totals. Zero means they match. */
  extraction_gap?: number | null;
  monthly_cash_flow?: MonthCashFlow[];
}

export interface MonthCashFlow {
  month: string;
  income: number;
  spend: number;
  debt: number;
  surplus: number;
}

export interface FinancialInsight {
  area: string;
  title: string;
  detail: string;
  severity: 'info' | 'watch' | 'alert' | string;
}

export interface DocumentHealthCheck {
  label: string;
  passed: boolean;
}

export interface DocumentHealth {
  verified: boolean;
  issues: string[];
  checks: DocumentHealthCheck[];
}

export interface StatementOpinion {
  available: boolean;
  opinion: string;
  stance: string;
}

export interface AffordabilityReport {
  session_id: string;
  document_ids: string[];
  documents: {
    id: string;
    document_type: string;
    source: string;
    transactions: number;
    statement_period?: string;
    customer_name?: string;
    account_number?: string;
    opening_balance?: string;
    closing_balance?: string;
    total_paid_in?: string;
    total_paid_out?: string;
  }[];
  average_monthly_income: number;
  monthly_debt: number;
  debt_to_income: number;
  confidence: number;
  recommendations: string[];
  insights?: FinancialInsight[];
  metrics: FinancialMetrics;
  income: {
    average_monthly_income: number;
    highest_monthly_income: number;
    lowest_monthly_income: number;
    months_analyzed: number;
    income_stability: number;
    income_stability_label?: string;
    primary_income_source: string;
    salary_frequency: string;
    business_income_frequency: string;
    total_qualified_income: number;
    recurring_income_monthly?: number;
    income_sources?: {
      name: string;
      kind: string;
      monthly_amount: number;
      count: number;
      months: number;
    }[];
    confidence: number;
  };
  debt: {
    existing_monthly_debt: number;
    debt_providers: { name: string; monthly_amount: number; count: number }[];
    outstanding_loans: number;
    estimated_outstanding?: number | null;
    loan_frequency: string;
    total_debt_payments: number;
    loan_stacking?: boolean;
    confidence: number;
  };
  behaviour: {
    behaviour_score: number;
    income_consistency: number;
    salary_regularity: number;
    multiple_income_sources: boolean;
    spending_breakdown: {
      category: string;
      total: number;
      count: number;
      average: number;
      essential?: boolean;
      monthly_avg?: number;
    }[];
    savings: {
      average_monthly_savings: number;
      savings_frequency: string;
      savings_consistency: number;
      total_saved: number;
      months_saving: number;
    };
    gambling: {
      total_gambling: number;
      number_of_bets: number;
      monthly_gambling_spend: number;
      providers: string[];
      explanation?: string;
      confidence?: number;
    };
    mobile_loans: {
      loan_frequency: number;
      monthly_loan_activity: number;
      repayment_behavior: string;
      providers: string[];
      total_disbursed: number;
      total_repaid: number;
    };
    risk_events: { type: string; description: string; amount: number; date: string }[];
    risk_event_count: number;
  };
  transactions_count: number;
  analysis_period_months: number;
  statement_period_start?: string;
  statement_period_end?: string;
  statement_period_days?: number;
  created_at: string;
}

export interface CategorizedTransaction {
  id: string;
  receipt_no: string;
  completed_at: string;
  details: string;
  status: string;
  paid_in: number;
  withdrawn: number;
  balance: number;
  category: string;
  month: string;
  is_income: boolean;
  is_debt: boolean;
  is_gambling: boolean;
  is_loan: boolean;
  source: string;
  confidence: number;
}
